// Intervals — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Intervals Explanation modal. Same shape as greedyDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version. Three drills cross-reference curated problems.

const intervalDrills = [
  {
    id: 'summary-ranges',
    title: 'Summary Ranges',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Re-Verify the Whole Range on Every Extension',
        explanation: 'Grow a candidate range one element at a time, but re-check EVERY gap inside the range from scratch on every extension attempt, instead of remembering that everything up to the previous element was already confirmed consecutive.',
        steps: [
          'Starting a new range at some index, try extending it by one more element.',
          'To confirm the extension is valid, rescan every consecutive pair inside the WHOLE candidate range, not just the newest pair.',
          'Stop extending the moment any gap fails, and record the range.',
          'Start the next range right after.',
        ],
        code:
`List<String> summaryRangesBasic(int[] nums) {
    List<String> result = new ArrayList<>();
    int start = 0;
    while (start < nums.length) {
        int end = start;
        while (isConsecutive(nums, start, end + 1)) {
            end++;
        }
        result.add(start == end ? String.valueOf(nums[start]) : nums[start] + "->" + nums[end]);
        start = end + 1;
    }
    return result;
}

private boolean isConsecutive(int[] nums, int start, int end) {
    if (end >= nums.length) return false;
    for (int k = start; k < end; k++) {
        if (nums[k + 1] - nums[k] != 1) return false;   // re-checks every gap again
    }
    return true;
}`,
        complexity: { time: 'O(n²) worst case', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Single Pass, Extend While Consecutive',
        explanation: 'Only ever check the ONE new gap being extended into — never re-verify gaps that were already confirmed. A single forward pass is enough.',
        steps: [
          'Start a range at the current index.',
          'While the next element is exactly one more than the current one, extend the range by moving the end pointer forward.',
          'The moment the chain breaks, record the range (single value or "start->end").',
          'Start the next range at the break point.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [0, 1, 2, 4, 5, 7] }],
          varNames: [],
          steps: [
            { highlights: { nums: [0, 1, 2] }, note: 'Starting at index 0: nums[1]=1 and nums[2]=2 are each exactly +1 from the last. nums[3]=4 breaks the chain. Range: "0->2".' },
            { highlights: { nums: [3, 4] }, note: 'Starting at index 3: nums[4]=5 is consecutive. nums[5]=7 breaks the chain. Range: "4->5".' },
            { highlights: { nums: [5] }, note: 'Starting at index 5: no more elements. Single-value range: "7". Final: ["0->2", "4->5", "7"].' },
          ],
        },
        code:
`List<String> summaryRanges(int[] nums) {
    List<String> result = new ArrayList<>();
    int i = 0, n = nums.length;
    while (i < n) {
        int j = i;
        while (j + 1 < n && nums[j + 1] == nums[j] + 1) {
            j++;
        }
        result.add(i == j ? String.valueOf(nums[i]) : nums[i] + "->" + nums[j]);
        i = j + 1;
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'merge-intervals-drill',
    title: 'Merge Intervals',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Merge Intervals\" there for the complete step-by-step and both code versions. You can also try it hands-on " +
      "in the \"Merge Intervals\" mode of the Interactive Practice playground.",
  },
  {
    id: 'interval-list-intersections',
    title: 'Interval List Intersections',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Every Pair From Both Lists',
        explanation: 'Compare every interval from the first list against every interval from the second, computing the overlap for each pair directly. Correct, but the vast majority of pairs don\'t overlap at all, and each is checked anyway.',
        steps: [
          'For each interval in the first list, and each interval in the second list:',
          'Compute the overlap as [max(starts), min(ends)].',
          'If that range is valid (start ≤ end), record it.',
          'Every pair is checked, regardless of how far apart the two lists have already diverged.',
        ],
        code:
`List<int[]> intervalIntersectionBasic(int[][] firstList, int[][] secondList) {
    List<int[]> result = new ArrayList<>();
    for (int[] a : firstList) {
        for (int[] b : secondList) {
            int start = Math.max(a[0], b[0]);
            int end = Math.min(a[1], b[1]);
            if (start <= end) result.add(new int[]{start, end});
        }
    }
    return result;
}`,
        complexity: { time: 'O(n · m)', space: 'O(1) extra beyond the output' },
      },
      {
        name: 'Optimized — Two Pointers, Advance the Earlier-Ending List',
        explanation: 'Since both lists are already individually sorted and non-overlapping, only ONE pointer into each list is ever needed. After checking the current pair, always advance whichever interval ends FIRST — it can\'t possibly overlap anything further along in the other list.',
        steps: [
          'Compute the overlap of the two intervals currently pointed at: [max(starts), min(ends)].',
          'If valid (start ≤ end), record it.',
          'Advance the pointer of whichever interval has the SMALLER end — it\'s now fully "used up" and can\'t contribute further overlaps.',
          'Repeat until either list is exhausted.',
        ],
        visual: {
          arrays: [
            { label: 'firstList', values: ['[0,2]', '[5,10]', '[13,23]', '[24,25]'] },
            { label: 'secondList', values: ['[1,5]', '[8,12]', '[15,24]', '[25,26]'] },
          ],
          varNames: [],
          steps: [
            { highlights: { firstList: [0], secondList: [0] }, note: '[0,2] ∩ [1,5] = [max(0,1), min(2,5)] = [1,2]. Valid — add it. Advance firstList (end 2 < end 5).' },
            { highlights: { firstList: [1], secondList: [0] }, note: '[5,10] ∩ [1,5] = [5,5]. Valid — add it. Advance secondList (end 5 ≤ end 10).' },
            { highlights: { firstList: [1], secondList: [1] }, note: '[5,10] ∩ [8,12] = [8,10]. Valid — add it. Advance firstList (end 10 < end 12).' },
            { highlights: { firstList: [2], secondList: [1] }, note: '[13,23] ∩ [8,12] = [13,12] — invalid (13 > 12), skip. Advance secondList (end 12 < end 23).' },
            { highlights: { firstList: [2], secondList: [2] }, note: '[13,23] ∩ [15,24] = [15,23]. Valid — add. Continuing the same way through the rest gives the final result: [[1,2],[5,5],[8,10],[15,23],[24,24],[25,25]].' },
          ],
        },
        code:
`List<int[]> intervalIntersection(int[][] firstList, int[][] secondList) {
    List<int[]> result = new ArrayList<>();
    int i = 0, j = 0;
    while (i < firstList.length && j < secondList.length) {
        int start = Math.max(firstList[i][0], secondList[j][0]);
        int end = Math.min(firstList[i][1], secondList[j][1]);
        if (start <= end) result.add(new int[]{start, end});
        if (firstList[i][1] < secondList[j][1]) i++;
        else j++;
    }
    return result;
}`,
        complexity: { time: 'O(n + m)', space: 'O(1) extra beyond the output' },
      },
    ],
  },
  {
    id: 'insert-interval-drill',
    title: 'Insert Interval',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Insert Interval\" there for the complete step-by-step and both code versions. You can also try it hands-on " +
      "in the \"Insert Interval\" mode of the Interactive Practice playground.",
  },
  {
    id: 'my-calendar-i',
    title: 'My Calendar I',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Linear Scan Against Every Existing Booking',
        explanation: 'Keep bookings in a plain list, and for each new booking request, scan every existing booking checking for an overlap. Correct, but each check is O(n), so n bookings cost O(n²) total.',
        steps: [
          'For a new booking [start, end), scan every existing booking [s, e).',
          'They overlap if start < e AND s < end — if so, reject the new booking.',
          'If no existing booking overlaps, add the new one to the list and accept.',
          'Every booking call rescans the entire growing list.',
        ],
        code:
`class MyCalendarBasic {
    List<int[]> bookings = new ArrayList<>();

    boolean book(int start, int end) {
        for (int[] existing : bookings) {
            if (start < existing[1] && existing[0] < end) {
                return false;
            }
        }
        bookings.add(new int[]{start, end});
        return true;
    }
}`,
        complexity: { time: 'O(n) per call, O(n²) total for n bookings', space: 'O(n)' },
      },
      {
        name: 'Optimized — TreeMap: Check Only the Neighbors',
        explanation: 'Keep bookings in a TreeMap keyed by start time. A new booking can only possibly conflict with the booking that starts just BEFORE it, or the one that starts just AFTER it — every other existing booking is provably too far away to matter.',
        steps: [
          'Find the existing booking with the largest start ≤ the new start (floorEntry) — if its end reaches past the new start, they overlap.',
          'Find the existing booking with the smallest start ≥ the new start (ceilingEntry) — if its start is before the new end, they overlap.',
          'If neither neighbor conflicts, no other booking could possibly conflict either — safe to insert.',
          'Insert the new booking into the map and return true.',
        ],
        visual: {
          arrays: [{ label: 'bookings (start,end pairs)', values: [] }],
          varNames: [],
          steps: [
            { snapshots: { 'bookings (start,end pairs)': ['(10,20)'] }, note: 'book(10,20): no existing bookings — nothing to conflict with. Add directly. Returns true.' },
            { note: 'book(15,25): floorEntry(15) = (10,20). Its end (20) > 15 — overlaps! Reject. Returns false. Bookings unchanged.' },
            { snapshots: { 'bookings (start,end pairs)': ['(10,20)', '(20,30)'] }, note: 'book(20,30): floorEntry(20) = (10,20), end=20 is NOT > 20 — no conflict. ceilingEntry(20) = none. Safe to add. Returns true.' },
          ],
        },
        code:
`class MyCalendar {
    TreeMap<Integer, Integer> bookings = new TreeMap<>();

    boolean book(int start, int end) {
        Map.Entry<Integer, Integer> prev = bookings.floorEntry(start);
        Map.Entry<Integer, Integer> next = bookings.ceilingEntry(start);
        if (prev != null && prev.getValue() > start) return false;
        if (next != null && next.getKey() < end) return false;
        bookings.put(start, end);
        return true;
    }
}`,
        complexity: { time: 'O(log n) per booking', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'minimum-arrows-burst-balloons',
    title: 'Minimum Number of Arrows to Burst Balloons',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sort by End, Rescan for Each Arrow',
        explanation: 'Sort by end coordinate (the correct key), but implement "which balloons does this arrow also burst" by rescanning every remaining balloon each time, instead of relying on the sorted order to only need to check the immediate next one.',
        steps: [
          'Sort balloons by end coordinate.',
          'For the first not-yet-burst balloon, shoot an arrow at its end coordinate.',
          'Rescan every remaining balloon, marking any whose start is ≤ the arrow position as also burst.',
          'Repeat with the next not-yet-burst balloon until all are burst.',
        ],
        code:
`int findMinArrowShotsBasic(int[][] points) {
    Arrays.sort(points, (a, b) -> Integer.compare(a[1], b[1]));
    boolean[] burst = new boolean[points.length];
    int arrows = 0;
    for (int i = 0; i < points.length; i++) {
        if (burst[i]) continue;
        arrows++;
        int arrowPos = points[i][1];
        for (int j = i; j < points.length; j++) {
            if (!burst[j] && points[j][0] <= arrowPos) {
                burst[j] = true;
            }
        }
    }
    return arrows;
}`,
        complexity: { time: 'O(n²)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Greedy: Track Only the Last Arrow\'s Position',
        explanation: 'Since balloons are sorted by end, an arrow shot at the CURRENT balloon\'s end coordinate will automatically burst every subsequent balloon that starts before or at that position — no need to track burst status at all, just remember the last arrow\'s position.',
        steps: [
          'Sort balloons by end coordinate.',
          'Shoot the first arrow at the first balloon\'s end. Track it as lastArrowPos.',
          'For each subsequent balloon: if its start is beyond lastArrowPos, it needs a NEW arrow (shot at its own end); otherwise it\'s already burst.',
          'Count how many arrows were needed in total.',
        ],
        visual: {
          arrays: [{ label: 'points sorted by end', values: ['[1,6]', '[2,8]', '[7,12]', '[10,16]'] }],
          varNames: ['arrows', 'lastArrowPos'],
          steps: [
            { vars: { arrows: 1, lastArrowPos: 6 }, note: 'Sorted by end: [1,6] first. Shoot arrow 1 at x=6. arrows=1, lastArrowPos=6.' },
            { highlights: { 'points sorted by end': [1] }, vars: { arrows: 1, lastArrowPos: 6 }, note: '[2,8]: start=2 ≤ lastArrowPos(6) — already burst by arrow 1. No new arrow needed.' },
            { highlights: { 'points sorted by end': [2] }, vars: { arrows: 2, lastArrowPos: 12 }, note: '[7,12]: start=7 > lastArrowPos(6) — needs a NEW arrow. arrows=2, lastArrowPos becomes 12.' },
            { highlights: { 'points sorted by end': [3] }, vars: { arrows: 2, lastArrowPos: 12 }, note: '[10,16]: start=10 ≤ lastArrowPos(12) — already burst by arrow 2. Final answer: 2 arrows.' },
          ],
        },
        code:
`int findMinArrowShots(int[][] points) {
    if (points.length == 0) return 0;
    Arrays.sort(points, (a, b) -> Integer.compare(a[1], b[1]));
    int arrows = 1;
    int lastArrowPos = points[0][1];
    for (int i = 1; i < points.length; i++) {
        if (points[i][0] > lastArrowPos) {
            arrows++;
            lastArrowPos = points[i][1];
        }
    }
    return arrows;
}`,
        complexity: { time: 'O(n log n)', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'employee-free-time',
    title: 'Employee Free Time',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Minute-by-Minute Scan',
        explanation: 'Check every single minute across the entire time range, testing whether ANY employee is busy at that minute. Correct, but extremely wasteful when the time range is large relative to the number of actual intervals.',
        steps: [
          'Find the overall minimum and maximum time across every employee\'s schedule.',
          'For each minute in that range, scan every interval from every employee to check if it covers this minute.',
          'Track runs of minutes where NO employee is busy — those are common free time.',
          'Record each run as it ends.',
        ],
        code:
`List<int[]> employeeFreeTimeBasic(List<List<int[]>> schedule) {
    int minTime = Integer.MAX_VALUE, maxTime = Integer.MIN_VALUE;
    List<int[]> all = new ArrayList<>();
    for (List<int[]> person : schedule) {
        for (int[] interval : person) {
            all.add(interval);
            minTime = Math.min(minTime, interval[0]);
            maxTime = Math.max(maxTime, interval[1]);
        }
    }
    List<int[]> result = new ArrayList<>();
    Integer freeStart = null;
    for (int t = minTime; t <= maxTime; t++) {
        boolean busy = false;
        for (int[] interval : all) {
            if (t >= interval[0] && t < interval[1]) { busy = true; break; }
        }
        if (!busy) {
            if (freeStart == null) freeStart = t;
        } else if (freeStart != null) {
            result.add(new int[]{freeStart, t});
            freeStart = null;
        }
    }
    return result;
}`,
        complexity: { time: 'O((maxTime - minTime) · totalIntervals)', space: 'O(totalIntervals)' },
      },
      {
        name: 'Optimized — Flatten, Sort, Merge, Read Off the Gaps',
        explanation: 'Ignore individual employees entirely — flatten every interval from every schedule into one list, sort by start, and merge overlapping ones exactly like Merge Intervals. Whatever gaps remain BETWEEN the merged busy blocks are, by definition, time nobody is busy.',
        steps: [
          'Flatten all employees\' intervals into a single list.',
          'Sort by start time, then merge overlapping intervals (the standard Merge Intervals routine).',
          'The merged result is a list of "everyone\'s combined busy time," with no gaps hidden inside it.',
          'The GAPS between consecutive merged intervals are exactly the common free time.',
        ],
        visual: {
          arrays: [
            { label: 'all intervals sorted by start', values: ['[1,3]', '[2,4]', '[6,7]', '[8,10]'] },
            { label: 'merged busy blocks', values: [] },
          ],
          varNames: [],
          steps: [
            { note: 'Flatten every employee\'s intervals and sort by start: [1,3], [2,4], [6,7], [8,10].' },
            { snapshots: { 'merged busy blocks': ['[1,4]'] }, note: '[1,3] and [2,4] overlap (2 ≤ 3) — merge into [1,4].' },
            { snapshots: { 'merged busy blocks': ['[1,4]', '[6,7]'] }, note: '[6,7]: starts after 4 — no overlap. Add as a new busy block.' },
            { snapshots: { 'merged busy blocks': ['[1,4]', '[6,7]', '[8,10]'] }, note: '[8,10]: starts after 7 — no overlap. Add as a new busy block.' },
            { note: 'The gaps BETWEEN consecutive merged blocks are the common free time: (4,6) and (7,8). Final answer: [[4,6],[7,8]].' },
          ],
        },
        code:
`List<int[]> employeeFreeTime(List<List<int[]>> schedule) {
    List<int[]> all = new ArrayList<>();
    for (List<int[]> person : schedule) all.addAll(person);
    all.sort((a, b) -> Integer.compare(a[0], b[0]));

    List<int[]> merged = new ArrayList<>();
    for (int[] interval : all) {
        if (!merged.isEmpty() && interval[0] <= merged.get(merged.size() - 1)[1]) {
            merged.get(merged.size() - 1)[1] =
                Math.max(merged.get(merged.size() - 1)[1], interval[1]);
        } else {
            merged.add(interval);
        }
    }

    List<int[]> freeTime = new ArrayList<>();
    for (int i = 1; i < merged.size(); i++) {
        freeTime.add(new int[]{merged.get(i - 1)[1], merged.get(i)[0]});
    }
    return freeTime;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'car-pooling',
    title: 'Car Pooling',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Minute Against Every Trip',
        explanation: 'For each time unit across the whole route, sum up the passengers from every trip that covers it, and compare against capacity. Correct, but re-derives the passenger count from scratch at every single time unit.',
        steps: [
          'Find the maximum end time across all trips.',
          'For each time unit from 0 up to that maximum, sum the passenger counts of every trip active at that moment.',
          'If the sum ever exceeds capacity, the trips can\'t all be completed — return false.',
          'If no time unit ever exceeds capacity, return true.',
        ],
        code:
`boolean carPoolingBasic(int[][] trips, int capacity) {
    int maxTime = 0;
    for (int[] trip : trips) maxTime = Math.max(maxTime, trip[2]);
    for (int t = 0; t < maxTime; t++) {
        int passengers = 0;
        for (int[] trip : trips) {
            if (t >= trip[1] && t < trip[2]) passengers += trip[0];
        }
        if (passengers > capacity) return false;
    }
    return true;
}`,
        complexity: { time: 'O(maxTime · n)', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Difference Array (Sweep the Passenger Count Once)',
        explanation: 'Instead of recomputing the passenger count from scratch at every location, record only the CHANGES: +passengers where a trip starts, -passengers where it ends. A single running prefix sum over these changes reconstructs the passenger count at every location, visiting each change exactly once.',
        steps: [
          'For each trip, add its passenger count at its start location, and subtract it at its end location, in a difference array.',
          'Sweep through the difference array once, keeping a running total.',
          'The moment the running total exceeds capacity, the trips can\'t all be completed — return false.',
          'If the running total never exceeds capacity across the whole sweep, return true.',
        ],
        visual: {
          arrays: [{ label: 'delta array (locations 0..7)', values: [0, 2, 0, 3, 0, -2, 0, -3] }],
          varNames: ['passengers'],
          steps: [
            { highlights: { 'delta array (locations 0..7)': [1] }, vars: { passengers: 2 }, note: 'At location 1: delta=+2 (trip [2,1,5] boards). Running passengers = 2 (≤ capacity 5, OK).' },
            { highlights: { 'delta array (locations 0..7)': [3] }, vars: { passengers: 5 }, note: 'At location 3: delta=+3 (trip [3,3,7] boards). Running passengers = 2+3 = 5 (still ≤ capacity 5 — right at the limit, OK).' },
            { highlights: { 'delta array (locations 0..7)': [5] }, vars: { passengers: 3 }, note: 'At location 5: delta=-2 (first trip exits). Running passengers = 5-2 = 3.' },
            { highlights: { 'delta array (locations 0..7)': [7] }, vars: { passengers: 0 }, note: 'At location 7: delta=-3 (second trip exits). Running passengers = 0. Max reached was 5, never exceeding capacity(5) — final answer: true.' },
          ],
        },
        code:
`boolean carPooling(int[][] trips, int capacity) {
    int[] delta = new int[1001];
    for (int[] trip : trips) {
        delta[trip[1]] += trip[0];
        delta[trip[2]] -= trip[0];
    }
    int passengers = 0;
    for (int d : delta) {
        passengers += d;
        if (passengers > capacity) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n + maxLocation)', space: 'O(maxLocation)' },
      },
    ],
  },
  {
    id: 'meeting-rooms-ii-drill',
    title: 'Meeting Rooms II',
    level: 'Advanced',
    crossReference:
      "This drill's full walkthrough — including a min-heap approach alongside the flagship two-pointer sweep — " +
      "lives in this pattern's curated problem list — open \"Meeting Rooms II\" there for the complete step-by-step. " +
      "You can also try it hands-on in the \"Meeting Rooms II\" mode of the Interactive Practice playground.",
  },
];

export default intervalDrills;
